import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { Mark } from '../../chrome/brand/Mark';
import { Page } from '../../chrome/page';
import { t } from '../../i18n';
import { ThemeToggle } from '../../chrome/shell';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '../../ui/card';

/** The card's one footer action: a link, never a class string a route hand-rolls. */
type AuthFooter = {
  /** Text before the link, e.g. "Primeira vez aqui?" -- omitted for a back link. */
  prompt?: string;
  to: string;
  label: string;
  icon?: ReactNode;
};

/**
 * The one frame every screen a stranger can reach uses: sign-in, sign-up,
 * reset-password and invitation acceptance render nothing else around their
 * own content. `CardTitle` is not used for the heading -- it renders a `div`,
 * and each of these screens is a standalone page, so its heading is the
 * page's own `h1`, not a card decoration.
 */
export function AuthFrame(props: {
  title: string;
  description?: string;
  children: ReactNode;
  align?: 'start' | 'center';
  footer?: AuthFooter;
  /** A medallion above the title, e.g. the organisation icon on the
   *  invitation-acceptance screen (Figma `128:394`). Omitted everywhere
   *  else this frame renders (sign-in, sign-up, reset-password). */
  icon?: ReactNode;
}): ReactNode {
  const align = props.align ?? 'start';
  return (
    <Page width="narrow">
      <main className="mx-auto flex min-h-svh w-full max-w-sm flex-col items-center justify-center gap-6 px-4 py-12">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Mark aria-hidden />
          {t('shell.appName')}
        </Link>
        <Card className="w-full">
          <CardHeader className={align === 'center' ? 'text-center' : undefined}>
            {props.icon !== undefined && (
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted">
                {props.icon}
              </div>
            )}
            <h1 className="text-lg leading-none font-semibold">{props.title}</h1>
            {props.description !== undefined && (
              <CardDescription>{props.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-6">{props.children}</CardContent>
          {props.footer !== undefined && (
            <CardFooter className="justify-center">
              <p className="text-sm text-muted-foreground">
                {props.footer.prompt !== undefined && <>{props.footer.prompt} </>}
                <Button asChild variant="link" size="sm">
                  <Link to={props.footer.to} className="inline-flex items-center gap-1">
                    {props.footer.icon}
                    {props.footer.label}
                  </Link>
                </Button>
              </p>
            </CardFooter>
          )}
        </Card>
        <ThemeToggle />
      </main>
    </Page>
  );
}
